import { BaseService } from './BaseService';
import { tokenManager } from './TokenManager';
import { isCapacitor, isAndroid, isIOS } from '../utils/platform';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { FileOpener } from '@capawesome-team/capacitor-file-opener';
 
// Word Service for template management and document export
export class WordService extends BaseService {
  constructor() {
    super();
  }

  // ===== USER TEMPLATE MANAGEMENT =====

  /**
   * Get all templates available to the user (user templates + system default)
   */
  async getUserTemplates() {
    const response = await this.get('/word/templates');
    return response.data.templates;
  }

  /**
   * Upload a new template for the user (max 3 per user)
   * @param {File} templateFile - The .docx file to upload
   * @param {string} reportTypeId - Optional report type ID to link template to
   * @param {string} reportTypeName - Optional report type name to link template to
   */
  async uploadUserTemplate(templateFile, reportTypeId = null, reportTypeName = null) {
    const formData = new FormData();
    formData.append('template', templateFile);

    // Add report type if provided - SINGLE API CALL
    if (reportTypeId && reportTypeId !== '') {
      formData.append('reportTypeId', reportTypeId);
    }
    if (reportTypeName) {
      formData.append('reportType', reportTypeName);
    }

    const response = await this.postFormData('/word/templates/upload', formData);
    return response.data.template;
  }

  /**
   * Update template name or active status
   * @param {string} templateId - The template ID
   * @param {Object} updates - Object containing name, isDefault, and/or reportType
   */
  async updateUserTemplate(templateId, updates) {
    const response = await this.put(`/word/templates/${templateId}`, updates);
    return response.data.template;
  }

  /**
   * Delete a user's template
   * @param {string} templateId - The template ID
   */
  async deleteUserTemplate(templateId) {
    await this.delete(`/word/templates/${templateId}`);
  }


  /**
   * Download a user template file (.docx)
   * @param {string} templateId - The template ID
   */
  async downloadUserTemplate(templateId) {
    const blob = await this.getBlob(`/word/templates/${templateId}/download`);
    return blob;
  }

  // ===== DEFAULT TEMPLATE MANAGEMENT =====

  /**
   * Upload a default template for the user
   * This template will be used for all reports that don't have a specific template
   * Only ONE default template is allowed per user (uploading replaces existing)
   * @param {File} templateFile - The .docx file to upload (max 10MB)
   */
  async uploadUserDefaultTemplate(templateFile) {
    const formData = new FormData();
    formData.append('template', templateFile);

    const response = await this.postFormData('/word/templates/default/user', formData);
    return response.data.template;
  }

  /**
   * Delete the user's default template
   */
  async deleteUserDefaultTemplate() {
    const response = await this.delete('/word/templates/default/user');
    return response.data;
  }

  /**
   * Upload a default template for an enterprise
   * Only ONE default template is allowed per enterprise (uploading replaces existing)
   * @param {File} templateFile - The .docx file to upload (max 10MB)
   * @param {string} enterpriseId - The enterprise ID
   */
  async uploadEnterpriseDefaultTemplate(templateFile, enterpriseId) {
    const formData = new FormData();
    formData.append('template', templateFile);
    formData.append('enterpriseId', enterpriseId);

    const response = await this.postFormData('/word/templates/default/enterprise', formData);
    return response.data.template;
  }

  /**
   * Delete the enterprise's default template
   * @param {string} enterpriseId - The enterprise ID
   */
  async deleteEnterpriseDefaultTemplate(enterpriseId) {
    const response = await this.delete(`/word/templates/default/enterprise/${enterpriseId}`);
    return response.data;
  }

  /**
   * Get the user's default template
   * @returns {Promise<Object|null>} The default template object or null if none exists
   */
  async getUserDefaultTemplate() {
    const response = await this.get('/word/templates/default/user');
    return response.data.template; // Can be null
  }

  /**
   * Get the enterprise's default template
   * @param {string} enterpriseId - The enterprise ID
   * @returns {Promise<Object|null>} The default template object or null if none exists
   */
  async getEnterpriseDefaultTemplate(enterpriseId) {
    const response = await this.get(`/word/templates/default/enterprise/${enterpriseId}`);
    return response.data.template; // Can be null
  }

  /**
   * Download the system default template
   * This is the global default template provided by the system that users can use as a starting point
   * @returns {Promise<Blob>} The .docx file as a blob
   */
  async downloadSystemDefaultTemplate() {
    const blob = await this.getBlob('/word/templates/default/system/download');
    return blob;
  }

  // ===== ENTERPRISE TEMPLATE MANAGEMENT =====

  /**
   * Get all templates for the enterprise (enterprise templates only)
   */
  async getEnterpriseTemplates() {
    const response = await this.get('/enterprise/word/templates');
    
    return response.data.templates;
  }

  // ===== EXAMPLE TEMPLATES =====

  /**
   * Get all available example templates
   * @returns {Promise<Array>} Array of example template objects
   */
  async getExampleTemplates() {
    const response = await this.get('/word/templates/examples');    
    return response.data.examples;
  }

  /**
   * Download an example template by ID
   * @param {string} exampleId - The example ID (e.g., 'example+irm_cardiaque')
   * @returns {Promise<Blob>} The template file as a blob
   */
  async downloadExampleTemplate(exampleId) {
    const blob = await this.getBlob(`/word/templates/examples/${encodeURIComponent(exampleId)}/download`);
    return blob;
  }

  /**
   * Upload a new template for the enterprise (max 5 per enterprise)
   * @param {File} templateFile - The .docx file to upload
   */
  async uploadEnterpriseTemplate(templateFile, reportTypeId = null, reportTypeName = null) {

    const formData = new FormData();
    formData.append('template', templateFile);

    // Add report type if provided
    if (reportTypeId && reportTypeId !== 'tous') {
      formData.append('reportTypeId', reportTypeId);
    }
    if (reportTypeName) {
      formData.append('reportType', reportTypeName);
    }

    const response = await this.postFormData('/enterprise/word/templates/upload', formData);

    return response.data.template;
  }

  /**
   * Update enterprise template properties
   * @param {string} templateId - The template ID
   * @param {Object} updates - Object containing name, reportType, isDefault, and/or isActive
   */
  async updateEnterpriseTemplate(templateId, updates) {
    const response = await this.put(`/enterprise/word/templates/${templateId}`, updates);
    return response.data.template;
  }

  /**
   * Delete an enterprise template
   * @param {string} templateId - The template ID
   */
  async deleteEnterpriseTemplate(templateId) {
    await this.delete(`/enterprise/word/templates/${templateId}`);
  }

  /**
   * Download an enterprise template file (.docx)
   * @param {string} templateId - The template ID
   */
  async downloadEnterpriseTemplate(templateId) {
    const blob = await this.getBlob(`/enterprise/word/templates/${templateId}/download`);
    return blob;
  }

  // ===== ENTERPRISE LOGO MANAGEMENT =====

  /**
   * Get the current enterprise logo as an image blob
   * @param {string} enterpriseId - Enterprise ID
   * @returns {Promise<Blob>} Image blob or throws if not found
   */
  async getEnterpriseLogo(enterpriseId) {
    if (!enterpriseId) throw new Error('enterpriseId is required');
    return this.getBlob(`/enterprises/${enterpriseId}/logo`);
  }

  /**
   * Upload or replace the enterprise logo (max 5MB, image/*)
   * @param {string} enterpriseId - Enterprise ID
   * @param {File} logoFile - Image file to upload (form field name: 'logo')
   */
  async uploadEnterpriseLogo(enterpriseId, logoFile) {
    if (!enterpriseId) throw new Error('enterpriseId is required');
    if (!logoFile) throw new Error('logo file is required');
    const formData = new FormData();
    formData.append('logo', logoFile);
    return this.postFormData(`/enterprises/${enterpriseId}/logo`, formData);
  }

  /**
   * Delete the current enterprise logo
   * @param {string} enterpriseId - Enterprise ID
   */
  async deleteEnterpriseLogo(enterpriseId) {
    if (!enterpriseId) throw new Error('enterpriseId is required');
    return this.delete(`/enterprises/${enterpriseId}/logo`);
  }

  // ===== DOCUMENT EXPORT =====

  /**
   * Export document to Word format with automatic template selection
   * @param {Object} exportData - Export configuration
   * @param {string|Object} exportData.content - Document content (string or structured object)
   * @param {string} exportData.reportType - Report type for template selection
   * @param {string} exportData.template - Optional specific template ID
   * @param {Array<{name:string, header:string[], rows:string[][], borders?:boolean, widthsTwips?:number[]}>} exportData.tables - Optional tables to render in the template. Use {@tableName} placeholder in DOCX.
   * @param {string} exportData.image - Optional base64 image data
   * @param {string} exportData.format - Export format (default: 'docx')
   */
  async exportToWord(exportData) {
    const payload = {
      ...exportData,
      isEnterprise: Boolean(exportData && exportData.isEnterprise)
    };

    // Build URL and headers manually to access response headers (filename)
    const url = `${this.baseURL}/word/export`;
    // Ensure token is fresh before starting
    await this.ensureFreshToken();

    

    const doFetch = async () => {
      const token = tokenManager.getToken();
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) headers.Authorization = `Bearer ${token}`;
      return fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(payload)
      });
    };

    let response = await doFetch();
    if (response.status === 401) {
      // Try once to refresh and retry
      await this.ensureFreshToken();
      response = await doFetch();
    }
    
    if (!response.ok) {
      let text = '';
      try { text = await response.text(); } catch (_) {}
      throw new Error(text || `Export failed (${response.status})`);
    }

    let fileName = undefined;
    const contentTypeHeader = response.headers.get('content-type') || '';
    let blob;

    if (contentTypeHeader.includes('application/json')) {
      // Backend may return JSON: { buffer, filename, contentType }
      const json = await response.json();
      fileName = json && typeof json.filename === 'string' && json.filename.trim() ? json.filename.trim() : undefined;
      const contentType = (json && json.contentType) || (exportData?.format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
      const b = json && json.buffer;
      let bytes;
      try {
        if (typeof b === 'string') {
          // Base64 string
          const base64 = b.includes(',') ? b.split(',')[1] : b; // handle data URLs
          const binaryString = atob(base64);
          const len = binaryString.length;
          bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
        } else if (b && typeof b === 'object' && b.type === 'Buffer' && Array.isArray(b.data)) {
          bytes = new Uint8Array(b.data);
        } else if (Array.isArray(b)) {
          bytes = new Uint8Array(b);
        } else if (b instanceof ArrayBuffer) {
          bytes = new Uint8Array(b);
        }
      } catch (_) {}
      if (!bytes) bytes = new Uint8Array();
      blob = new Blob([bytes], { type: contentType });
    } else {
      // Binary path: try to extract filename from headers
      const cd = response.headers.get('content-disposition');
      if (cd) {
        try {
          const matchStar = cd.match(/filename\*=(?:UTF-8''|)([^;]+)/i);
          if (matchStar && matchStar[1]) {
            const val = matchStar[1].trim();
            fileName = decodeURIComponent(val.replace(/^"|"$/g, ''));
          } else {
            const match = cd.match(/filename="?([^";]+)"?/i);
            if (match && match[1]) fileName = match[1];
          }
        } catch (_) {}
      }
      if (!fileName) {
        const altHeader = response.headers.get('x-filename');
        if (altHeader) fileName = altHeader;
      }
      blob = await response.blob();
    }

    // Compute a consistent final filename (fallback if header not readable on web)
    const inferredExt = exportData?.format === 'pdf' ? 'pdf' : 'docx';
    const baseFallback = (exportData && exportData.fileName) ? String(exportData.fileName) : 'rapport';
    let fileNameFinal = fileName || baseFallback;
    if (!new RegExp(`\.${inferredExt}$`, 'i').test(fileNameFinal)) {
      fileNameFinal = `${fileNameFinal}.${inferredExt}`;
    }

    // On Capacitor: save to device and open with default application
    if (isCapacitor && blob) {
      try {
        const finalName = fileNameFinal;
        const base64Data = await blobToBase64(blob);

        // Prefer Documents directory so the file is visible to the user
        const saveResult = await Filesystem.writeFile({
          path: finalName,
          data: base64Data,
          directory: Directory.Documents,
          recursive: true,
        });

        const filePath = saveResult.uri || saveResult.path || `${Directory.Documents}/${finalName}`;

        // Attempt to open with the default application
        await FileOpener.openFile({
          path: filePath,
          mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
      } catch (openErr) {
        // Swallow errors; still return the blob to the caller
      }
    }
    return { blob, fileName: fileNameFinal };
  }

  /**
   * Download a report as a file (PDF or other binary format)
   * @param {string} reportId - The report ID
   */
  async downloadReport(reportId) {
    const blob = await this.getBlob(`/reports/${reportId}/download`);
    return blob;
  }

  /**
   * Upload a report attachment with metadata
   * @param {string} reportId - The report ID
   * @param {File} file - The file to upload
   * @param {Object} metadata - Additional metadata
   */
  async uploadReportAttachment(reportId, file, metadata) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('metadata', JSON.stringify(metadata));
    
    const response = await this.postFormData(`/reports/${reportId}/attachments`, formData);
    return response;
  }
} 

// Helpers
async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      try {
        const result = reader.result;
        const base64 = typeof result === 'string' ? result.split(',')[1] : '';
        resolve(base64);
      } catch (e) {
        reject(e);
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}